import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// ── Chunk-load recovery ──
// When Vite hashed chunks change after a deploy, stale SW caches or browser
// caches may try to load old chunk URLs that no longer exist, crashing the app.
// This listener catches those failures, clears caches once, and hard-reloads.
const RELOAD_FLAG = '__chunk_reload';

window.addEventListener('vite:preloadError', (event: any) => {
  event?.preventDefault?.();
  handleChunkLoadFailure();
});

// Fallback: catch generic dynamic import errors the event above might miss
window.addEventListener('error', (event) => {
  if (
    event.message?.includes('Failed to fetch dynamically imported module') ||
    event.message?.includes('Importing a module script failed')
  ) {
    handleChunkLoadFailure();
  }
});

function handleChunkLoadFailure() {
  // Prevent infinite reload loops
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    sessionStorage.removeItem(RELOAD_FLAG);
    console.error('[Recovery] Chunk load failed even after cache clear + reload.');
    return;
  }
  sessionStorage.setItem(RELOAD_FLAG, '1');

  // Clear all akajiofo SW caches + workbox caches
  if ('caches' in window) {
    caches.keys().then((names) => {
      const deletions = names
        .filter((n) => n.startsWith('akajiofo-') || n.startsWith('workbox-'))
        .map((n) => caches.delete(n));
      return Promise.all(deletions).then(() => { window.location.reload(); });
    }).catch(() => { window.location.reload(); });
  } else {
    (window as Window).location.reload();
  }
}

// Clear the flag on successful load so future deploys can recover again
if (sessionStorage.getItem(RELOAD_FLAG)) {
  sessionStorage.removeItem(RELOAD_FLAG);
}

// ── App bootstrap ──
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
